import "./socialPublish.css"
import React, {useEffect, useRef, useState} from "react";
import {Clip, ClipDescription, Highlight, SocialClipsUrl, SocialPublication} from "../../types/clipTypes";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import {Typography} from "@mui/material";
import TransparentButtonSelection from "../CommonComponents/TransparentButtonSelection/TransparentButtonSelection";
import {formatDuration, formatUTCDate} from "../../utils/dateTimeutils";
import {useTheme} from "../../contexts/theme";
import TransparentButton from "../CommonComponents/TransparentButton/TransparentButton";
import {AspectRatio, LoadError, Option} from "../../types/generic";
import WindowPlayer from "../CommonComponents/WindowPlayer/WindowPlayer";
import SpinnerBox from "../CommonComponents/SpinnerBox/SpinnerBox";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LoopIcon from '@mui/icons-material/Loop';
import UploadIcon from '@mui/icons-material/Upload';
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import SocialSelection from "../CommonComponents/SocialSelection/SocialSelection";
import DailyMotionForm, {DailyMotionFormState} from "./SocialForms/DailyMotionForm";
import {
    deletePublication,
    getVideoContentSocialPosts,
    socialPost
} from "../../queries/socialQueries";
import {
    DailyMotionPostRequest,
    EnrichedSocialContentPublication, FacebookPostRequest, InstagramPostRequest, SocialAccountOption,
    SocialContentPublication, SocialPlatformNames, SocialPostNames, YoutubePostRequest
} from "../../types/socialTypes";
import {PublicationHistory} from "./PublicationHistory";
import axios from "axios";
import DefaultSocialForm, {DefaultSocialFormState} from "./SocialForms/DefaultForm";
import FacebookForm, {FacebookFormState} from "./SocialForms/FacebookForm";
import ErrorSnackBar from "../CommonComponents/ErrorSnackBar/ErrorSnackBar";
import InstagramForm, {InstagramFormState} from "./SocialForms/InstagramForm";
import YouTubeForm, {YouTubeFormState} from "./SocialForms/YouTubeForm";
import CachedIcon from '@mui/icons-material/Cached';
import {regenerateLLMDescription, updateLLMDescription} from "../../queries/clipQueries";
import { keyframes } from "@emotion/react";
import { styled } from "@mui/material/styles";
import SaveIcon from '@mui/icons-material/Save';

interface SocialPublishProps {
    clip: Clip | Highlight;
    aspectRatios: AspectRatio[];
    coordinates: number[];
    socialClips: SocialClipsUrl;
    defaultAspectRatio: AspectRatio;
}

interface KeyLabel{
    label: string;
    key: string;
}

interface SocialOptionsDict{
    platforms: {[key: string]: Option};
    accounts: {[key: string]: Option};
    postTypes: {[key: string]: Option};
}

type SocialFormState = DailyMotionFormState | DefaultSocialFormState | FacebookFormState | InstagramFormState;

const defaultLanguages: KeyLabel[] = [{key:'italian', label:"IT"}, {key: 'english', label:"EN"}]
const defaultStyles: KeyLabel[] = [{label:"Fan", key: "fan"}, {label:"Archivist", key:"archivist"}, {label:"Journalistic", key: 'journalistic'}, {label:"Commentator", key:'commentator'}]
const baseStyle = {label:"Journalistic", key: 'journalistic'}
const baseLanguage = {label:"IT", key:'italian'}
const defaultAspectRatioLabel = '16:9'

const supportedPostAspectRatio: any = {
    [SocialPlatformNames.facebook]: {
        [SocialPostNames.reel] : ['9:16'],
        [SocialPostNames.story] : ['9:16']
    }
}
const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;


const SocialPublish: React.FC<SocialPublishProps> = ({clip, defaultAspectRatio, aspectRatios,socialClips, coordinates}) => {

    const theme = useTheme();

    const SpinningIcon = styled(CachedIcon)(({ theme }) => ({
        animation: `${spin} 1s linear infinite`
    }));

    const [languages, setLanguages] = useState<KeyLabel[]>(defaultLanguages);
    const [selectedLanguage, setSelectedLanguage] = useState<KeyLabel>(baseLanguage);
    const [styles, setStyles] = useState<KeyLabel[]>(defaultStyles)
    const [selectedStyle, setSelectedStyle] = useState<KeyLabel>(baseStyle);
    const [selectedAspectRatio, setSelectedAspectRatio] = useState<AspectRatio>(defaultAspectRatio)
    const [isMaskedPlaying, setIsMaskedPlaying] = useState<boolean>(false);
    const previewVideoPlayerRef = useRef<HTMLVideoElement>(null);
    const [maskedPlayerTimeToggle, setMaskedPlayerTimeToggle] = useState<boolean>(false);
    const [changeMaskedPlayerTime, setChangeMaskedPlayerTime] = useState<number>(0);
    const maskedPlayerContainerRef = useRef<any>(null)
    const [maskedPlayerMuted, setMaskedPlayerMuted] = useState<boolean>(true);

    const [socialPlatform, setSocialPlatform] =  useState<Option | null>(null);
    const [socialPostType, setSocialPostType] = useState<Option | null>(null);
    const [socialAccount, setSocialAccount] = useState<Option | null>(null);

    const [enrichedSocialPublications, setEnrichedSocialPublications] = useState<EnrichedSocialContentPublication[]>([]);
    const backupSocialPublications = useRef<EnrichedSocialContentPublication[]>([])
    const socialOptionsDictRef = useRef<SocialOptionsDict>({postTypes:{}, platforms:{}, accounts:{}});
    const [socialFormState, setSocialFormState] = useState<SocialFormState>({title:'', description:''});

    const titleStyle = {color: theme.palette.white, fontSize: theme.fontSizes.subParagraphTitle, fontWeight: 'bold', textAlign: 'center', marginRight: theme.spacing.xs};
    const valueStyle = {color: theme.palette.lightGrey, fontSize: theme.fontSizes.subParagraphTitle,textAlign: 'center'};

    const [publications, setPublications] = useState<SocialContentPublication[]>([]);
    const abortControllerGetPosts = useRef<AbortController | null>(null);
    const postPollingInterval = useRef<any>(null)
    const deletingPublication = useRef<any>(false)
    const [publicationError, setPublicationError] = useState<LoadError>({message:'', error: false});
    const [publicationLoad, setPublicationLoad] = useState<boolean>(false);

    const [descriptionsLoading, setDescriptionsLoading] = useState<boolean>(false);
    const [descriptionsSaveLoading, setDescriptionsSaveLoading] = useState<boolean>(false);
    const [regeneratedDescriptions, setRegeneratedDescriptions] = useState<ClipDescription[]>([]);


    useEffect(() => {
        setSelectedAspectRatio(defaultAspectRatio)
    }, [defaultAspectRatio]);

    function handleAspectRatioChange(aspectRatioLabel: string){
        const aspectRatiosObject = aspectRatios.find(aspectRatio => aspectRatio.label === aspectRatioLabel);
        if (aspectRatiosObject){
            setSelectedAspectRatio(aspectRatiosObject)
            setIsMaskedPlaying(false)
            previewVideoPlayerRef.current!.load()
        }
    }

    function handleLanguageChange(languageLabel: string){
        const languageObject = languages.find(language => language.label === languageLabel);
        if (languageObject){
            const description = getClipField('description', languageObject.key, selectedStyle.key)
            const title = getClipField('title', languageObject.key, selectedStyle.key)
            llmPopulation(title, description)
            setSelectedLanguage(languageObject)
        }
    }

    function handleStyleChange(styleLabel: string){
        const styleObject = styles.find(style => style.label === styleLabel);
        if (styleObject){
            const description = getClipField('description', selectedLanguage.key, styleObject.key)
            const title = getClipField('title', selectedLanguage.key, styleObject.key)
            llmPopulation(title, description)
            setSelectedStyle(styleObject)
        }
    }

    function getClipField(field: 'description' | 'title', language: string, style:string){
        if (field === 'description'){
            //Priorità alla descrizione generata
            const regDescription = regeneratedDescriptions.find(desc => desc.style===style && desc.language === language);
            if (regDescription){
                return regDescription.description;
            }else{
                const styledDescription = clip.descriptions.find(description => description.style===style && description.language === language);
                return styledDescription ? styledDescription.description : ''
            }

        }
        if (field === 'title'){
            const styledDescription = clip.descriptions.find(description =>description.style==='title' && description.language === language);
            return styledDescription ? styledDescription.description : ''
        }
        return ''
    }

    useEffect(() => {
        const title = getClipField('title', selectedLanguage.key, selectedStyle.key);
        const description = getClipField('description', selectedLanguage.key, selectedStyle.key);
        llmPopulation(title, description);
    }, [clip]);

    function onMaskedPlayerCanPlay(){
        setIsMaskedPlaying(true);
    }

    async function onPreviewPlayerCanPlay(){

        if (previewVideoPlayerRef.current){
            maskedPlayerTimeToggle ? setChangeMaskedPlayerTime(0.0001) : setChangeMaskedPlayerTime(0)
            setMaskedPlayerTimeToggle((prevState) => !prevState);
            await previewVideoPlayerRef.current.play();
            setIsMaskedPlaying(true)
        }
    }

    function onMaskedVideoEnd(){
        if (previewVideoPlayerRef.current){
            previewVideoPlayerRef.current.currentTime = 0;
            previewVideoPlayerRef.current.play();
        }
    }

    async function onPostPublication(){
        try{
            const tempAspectRatio = JSON.parse(JSON.stringify(selectedAspectRatio.label))
            setPublicationLoad(true)
            if (socialPlatform && socialPlatform.value === SocialPlatformNames.dailyMotion){
                const dailyMotionFormState = socialFormState as DailyMotionFormState
                const dailyMotionPost: DailyMotionPostRequest = {
                    channel_id: dailyMotionFormState.channel_id,
                    title: dailyMotionFormState.title.length > 255 ? dailyMotionFormState.title.slice(0, 255) : dailyMotionFormState.title,
                    description: dailyMotionFormState.description,
                    social_account_id: socialAccount!.id,
                    social_content_id: socialPostType!.id,
                    is_created_for_kids: dailyMotionFormState.is_created_for_kids,
                    private: dailyMotionFormState.private,
                    tags: []
                }
                const newSocialPost = await socialPost(socialClips[selectedAspectRatio.label].id, dailyMotionPost);
                onPublicationAdd(newSocialPost, tempAspectRatio);
            }
            if (socialPlatform && socialPlatform.value === SocialPlatformNames.facebook){
                const facebookFormState = socialFormState as FacebookFormState
                const facebookPost: FacebookPostRequest = {
                    title: facebookFormState.title.length > 255 ? facebookFormState.title.slice(0, 255) : facebookFormState.title,
                    description: facebookFormState.description,
                    social_account_id: socialAccount!.id,
                    social_content_id: socialPostType!.id,
                }
                const newSocialPost = await socialPost(socialClips[selectedAspectRatio.label].id, facebookPost);
                setPublicationLoad(true)
                onPublicationAdd(newSocialPost, tempAspectRatio);
            }
            if (socialPlatform && socialPlatform.value === SocialPlatformNames.instagram){
                const instagramFormState = socialFormState as InstagramFormState
                const instagramPost: InstagramPostRequest= {
                    social_account_id: socialAccount!.id,
                    social_content_id: socialPostType!.id,
                }
                if (socialPostType && socialPostType.value !== SocialPostNames.story){
                    instagramPost.caption = instagramFormState.caption.length > 255 ? instagramFormState.caption.slice(0, 255) : instagramFormState.caption;
                }
                const newSocialPost = await socialPost(socialClips[selectedAspectRatio.label].id, instagramPost);
                setPublicationLoad(true)
                onPublicationAdd(newSocialPost, tempAspectRatio);
            }
            if (socialPlatform && socialPlatform.value === SocialPlatformNames.youtube){
                const youtubeFormState = socialFormState as YouTubeFormState
                const youtubePost: YoutubePostRequest = {
                    title: youtubeFormState.title.length > 255 ? youtubeFormState.title.slice(0, 255) : youtubeFormState.title,
                    description: youtubeFormState.description,
                    social_account_id: socialAccount!.id,
                    social_content_id: socialPostType!.id,
                    privacy_status: youtubeFormState.private ? 'private' : 'public',
                    tags: []
                }
                const newSocialPost = await socialPost(socialClips[selectedAspectRatio.label].id, youtubePost);
                onPublicationAdd(newSocialPost, tempAspectRatio);
            }
        }catch (e){
            setPublicationError({message:'Failed Social Publication', error: true});
        }finally {
            setPublicationLoad(false)
        }
    }

    function onSocialFormStateChange(field: string, value: string | boolean){
        if (!socialPlatform){
            const defaultSocialForm = {...socialFormState} as any;
            defaultSocialForm[field] = value;
            setSocialFormState(defaultSocialForm);
            return
        }
        if (socialPlatform && socialFormState && socialPlatform.value === SocialPlatformNames.dailyMotion){
            const dailyMotionForm = {...socialFormState} as any;
            dailyMotionForm[field] = value;
            setSocialFormState(dailyMotionForm);
            return
        }
        if (socialPlatform && socialFormState && socialPlatform.value === SocialPlatformNames.facebook){
            const facebookFormState = {...socialFormState} as any;
            facebookFormState[field] = value;
            setSocialFormState(facebookFormState);
            return
        }
        if (socialPlatform && socialFormState && socialPlatform.value === SocialPlatformNames.instagram){
            const instagramFormState = {...socialFormState} as any;
            instagramFormState[field] = value;
            setSocialFormState(instagramFormState);
            return
        }
        if (socialPlatform && socialFormState && socialPlatform.value === SocialPlatformNames.youtube){
            const youtubeForm = {...socialFormState} as any;
            youtubeForm[field] = value;
            setSocialFormState(youtubeForm);
            return
        }
    }

    function llmPopulation(title:string | null, description: string) {
        if (!socialPlatform){
            const defaultSocialForm = {...socialFormState} as any;
            defaultSocialForm.description = description;
            if(title){
                defaultSocialForm.title = title;
            }
            setSocialFormState(defaultSocialForm);
        }
        if (socialPlatform && socialFormState && socialPlatform.value === SocialPlatformNames.dailyMotion) {
            const dailyMotionForm = {...socialFormState} as any;
            dailyMotionForm.description = description;
            if(title){
                dailyMotionForm.title = title;
            }
            setSocialFormState(dailyMotionForm);
        }
        if (socialPlatform && socialFormState && socialPlatform.value === SocialPlatformNames.facebook) {
            const facebookForm = {...socialFormState} as any;
            facebookForm.description = description;
            if(title){
                facebookForm.title = title;
            }
            setSocialFormState(facebookForm);
        }
        if (socialPlatform && socialFormState && socialPlatform.value === SocialPlatformNames.instagram) {
            const instagramForm = {...socialFormState} as any;
            if(title){
                instagramForm.caption = description;
            }
            setSocialFormState(instagramForm);
        }
        if (socialPlatform && socialFormState && socialPlatform.value === SocialPlatformNames.youtube) {
            const youtubeForm = {...socialFormState} as any;
            youtubeForm.description = description;
            youtubeForm.title = title;
            setSocialFormState(youtubeForm);
        }
    }

    async function handleRegenerateDescription(){
        try{
            setDescriptionsLoading(true)
            const newDescription = await regenerateLLMDescription(clip.id, selectedLanguage.key, selectedStyle.key)
            const tempDescriptions = [...regeneratedDescriptions]
            const oldDescriptionIndex = tempDescriptions.findIndex(desc => desc.style === newDescription.style && desc.language === newDescription.language)
            if (oldDescriptionIndex !== -1){
                tempDescriptions[oldDescriptionIndex] = newDescription
            }else{
                tempDescriptions.push(newDescription)
            }
            llmPopulation(null, newDescription.description);
            setRegeneratedDescriptions(tempDescriptions);
            setDescriptionsLoading(false)
        }catch (e) {
            console.error(e);
            setDescriptionsLoading(false)
            setPublicationError({error: true, message: 'Error to regenerate description'})
        }
    }



    async function handleDescriptionsSave(){
        try{

            const newDescriptions = [...clip.descriptions]
            const actualDescription = newDescriptions.find(desc => desc.style === selectedStyle.key && desc.language === selectedLanguage.key)
            const formState = socialFormState as any
            const formDescription = formState.description ? formState.description : (formState.caption? formState.caption : '');
            const fallbackContent = {
                style: selectedStyle.key,
                language: selectedLanguage.key,
                description: formDescription
            }
            if (formDescription){
                setDescriptionsSaveLoading(true)
                if (actualDescription){
                    actualDescription.description = formDescription
                }else{
                    newDescriptions.push(fallbackContent)
                }
                await updateLLMDescription(clip.id, newDescriptions)

                const newDescription = actualDescription ? actualDescription : fallbackContent
                const tempDescriptions = [...regeneratedDescriptions]
                const oldDescriptionIndex = tempDescriptions.findIndex(desc => desc.style === newDescription.style && desc.language === newDescription.language)
                if (oldDescriptionIndex !== 1){
                    tempDescriptions[oldDescriptionIndex] = newDescription
                }else{
                    tempDescriptions.push(newDescription)
                }
                setRegeneratedDescriptions(tempDescriptions)
                setDescriptionsSaveLoading(false)
            }
        }catch (e) {
            console.error(e);
            setDescriptionsSaveLoading(false)
            setPublicationError({error: true, message: 'Error to save descriptions'})
        }
    }

    function onSocialPlatformChange(platform: Option | null){
        if (platform && platform.value === SocialPlatformNames.dailyMotion){
            const actualDailyMotionState = socialFormState as DailyMotionFormState;
            const dailyMotionForm: DailyMotionFormState = {
                is_created_for_kids: false,
                private: false,
                description: actualDailyMotionState.description,
                title: actualDailyMotionState.title,
                channel_id: ""
            };
            setSocialFormState(dailyMotionForm)
        }
        if (platform && platform.value === SocialPlatformNames.facebook){
            const actualFacebookState = socialFormState as FacebookFormState;
            const facebookForm: FacebookFormState = {
                description: actualFacebookState.description,
                title: actualFacebookState.title,
            };
            setSocialFormState(facebookForm)
        }
        if (platform && platform.value === SocialPlatformNames.instagram){
            const actualInstagramState = socialFormState as any;
            const instagramForm: InstagramFormState = {
                caption: actualInstagramState.description
            };
            setSocialFormState(instagramForm)
        }
        setSocialPlatform(platform)
    }

    function convertOptionArrayToDict(options: Option[]){
        const optionDict: {[key: string]: Option} = {};
        for(const option of options){
            optionDict[option.id] = option
        }
        return optionDict;
    }

    function changeEnrichedPublications(){
        if(Object.keys(socialOptionsDictRef.current.accounts).length > 0){
            setEnrichedSocialPublications(publications.map(pub => {
                const socialPlatformId = (socialOptionsDictRef.current.accounts[pub.social_account_id] as SocialAccountOption).socialPlatform
                return {
                    ...pub,
                    social_account_name: socialOptionsDictRef.current.accounts[pub.social_account_id].value,
                    social_platform_name: socialOptionsDictRef.current.platforms[socialPlatformId].value,
                    social_content_name: socialOptionsDictRef.current.postTypes[pub.social_content_id].value,
                    published_at: formatUTCDate(pub.published_at, false),
                    view: pub.insights ? pub.insights.viewCount : "" ,
                    like: pub.insights ? pub.insights.likeCount : "",
                    comment: pub.insights ? pub.insights.commentCount : "",
                    share: pub.insights ? pub.insights.shareCount : "",
                    save: pub.insights ? pub.insights.saveCount : "",
                }
            }))
        }
    }

    function onSocialOptionsLoad(platformOptions: Option[], accountOptions: Option[], postTypeOptions: Option[]){
        socialOptionsDictRef.current.platforms = convertOptionArrayToDict(platformOptions);
        socialOptionsDictRef.current.accounts = convertOptionArrayToDict(accountOptions);
        socialOptionsDictRef.current.postTypes = convertOptionArrayToDict(postTypeOptions);
        changeEnrichedPublications()
    }

    async function handlePublicationDelete(publicationId: string){
        try{
            deletingPublication.current = true;
            onPublicationDelete(publicationId)
            await deletePublication(publicationId);
        }catch (e) {
            setPublicationError({error: true, message: 'Error deleting the publication'});
        }finally {
            deletingPublication.current = false
        }
    }

    useEffect(() => {
        changeEnrichedPublications()
    }, [publications]);

    function startPostPolling(){
        postPollingInterval.current = setInterval(fetchPublications, 5000);
    }

    function inProgressPublication(publicationList: SocialContentPublication[]){
        return publicationList.some(pub => pub.status === 'in_progress')
    }


    function handleClose(){
        if(postPollingInterval.current){
            clearInterval(postPollingInterval.current);
        }
    }

    function onPublicationAdd(socialPost: SocialContentPublication, aspectRatio: string){
        const tempPublications = [...publications];
        const tempPost = {...socialPost};
        tempPost.aspect_ratio = aspectRatio
        tempPublications.push(tempPost);
        setPublications(tempPublications);
        if(socialPost.status === 'in_progress'){
            if(postPollingInterval.current){
                clearInterval(postPollingInterval.current);
            }
            if(abortControllerGetPosts.current){
                abortControllerGetPosts.current.abort()
            }
            startPostPolling()
        }
    }

    function onPublicationDelete(socialPostId: string){
        const newPosts = publications.filter(pub => pub.content_publication_id !== socialPostId)
        setPublications(newPosts)
        if (abortControllerGetPosts.current){
            abortControllerGetPosts.current.abort()
        }
        if (!inProgressPublication(newPosts)){
            if(postPollingInterval.current){
                clearInterval(postPollingInterval.current);
            }
        }
    }

    async function fetchPublications(){
        try{
            if (!deletingPublication.current){
                abortControllerGetPosts.current = new AbortController();
                const tempPublications = await getVideoContentSocialPosts(clip.id, abortControllerGetPosts.current)
                setPublications(tempPublications)
                if (!inProgressPublication(tempPublications) && postPollingInterval.current){
                    clearInterval(postPollingInterval.current);
                    if (abortControllerGetPosts.current){
                        abortControllerGetPosts.current.abort();
                    }
                }
            }
        }catch (error){
            if (!axios.isCancel(error)){
                console.error("Error to find get data for ", error)
            }
        }
    }

    function getSocialPlayerHeight(){
        if (maskedPlayerContainerRef.current)
            return maskedPlayerContainerRef.current.clientHeight
        else{
            if (!socialClips[defaultAspectRatioLabel]){
                return '300px'
            }else{
                return 0
            }
        }
    }

    function disablePostButton(){
        if (descriptionsLoading)
            return true
        if (socialAccount === null || socialPlatform === null || socialPostType === null || publicationLoad){
            return true
        }else{
            const socialPlatformName = socialPlatform.value as SocialPlatformNames
            const socialPostTypeName = socialPostType.value as SocialPostNames
            const platformSupportedAspectRation = supportedPostAspectRatio[socialPlatformName]
            if (platformSupportedAspectRation && (platformSupportedAspectRation as any)[socialPostTypeName]){
                return !(platformSupportedAspectRation as any)[socialPostTypeName].find((aspectRatio: string) => aspectRatio === selectedAspectRatio.label)
            }else{
                return false
            }
        }
    }

    useEffect(() => {
        const startLoad = async () => {
            try{
                const promiseArray = [getVideoContentSocialPosts(clip.id)]
                const response = await Promise.all(promiseArray);
                const tempPublications = response[0] as SocialContentPublication[]
                setPublications(tempPublications);
                if (inProgressPublication(tempPublications)){
                    startPostPolling()
                }
            }catch (e){
                setPublicationError({error: true, message: 'Error to fetch the social clip'})
            }
        }
        startLoad();
        return handleClose
    }, []);

    return(
        <Box>
            <ErrorSnackBar autoHideDuration={5000} open={publicationError.error} setClose={() => setPublicationError({message:'', error: false})} errorMessage={publicationError.message}/>
            {socialClips[defaultAspectRatio.label] && <Box>
                <Box paddingTop={theme.spacing.md}>
                    <SocialSelection
                        socialAccount={socialAccount}
                        socialPostType={socialPostType}
                        socialPlatform={socialPlatform}
                        onSocialPostTypeChange={setSocialPostType}
                        onSocialPlatformChange={onSocialPlatformChange}
                        onSocialAccountChange={setSocialAccount}
                        onSocialOptionsLoad={onSocialOptionsLoad}
                    />
                </Box>
                <Grid container sx={{marginTop: theme.spacing.md}}>
                    <Grid item xs={6} lg={3} display={'flex'}>
                        <Typography sx={titleStyle}>Match time:</Typography>
                        <Typography sx={valueStyle}>{(clip as any).game_time ? (clip as any).game_time : ''}</Typography>
                    </Grid>
                    <Grid item xs={6} lg={3} display={'flex'}>
                        <Typography sx={titleStyle}>Key Moment:</Typography>
                        <Typography sx={valueStyle}>{(clip as any).key_moment_type ? (clip as any).key_moment_type : 'Highlight'}</Typography>
                    </Grid>
                    <Grid item xs={6} lg={3} display={'flex'}>
                        <Typography sx={titleStyle}>Side:</Typography>
                        <Typography sx={valueStyle}>{(clip as any).team ? (clip as any).team : ''}</Typography>
                    </Grid>
                    <Grid item xs={6} lg={3} display={'flex'}>
                        <Typography sx={titleStyle}>Duration: </Typography>
                        <Typography sx={valueStyle}>{socialClips[selectedAspectRatio.label] ? formatDuration(socialClips[selectedAspectRatio.label].duration) : ''}</Typography>
                    </Grid>
                </Grid>
                <Grid container sx={{marginTop: theme.spacing.md}}>
                    <Grid item xs={6} lg={3} display={'flex'} alignItems={'center'}>
                        <Typography sx={titleStyle}>Generation Settings</Typography>
                    </Grid>
                    <Grid item xs={6} lg={3} display={'flex'} alignItems={'center'}>
                        <Typography sx={titleStyle}>Language</Typography>
                        <TransparentButtonSelection options={languages.map(language => language.label)} onChange={handleLanguageChange} value={selectedLanguage.label}></TransparentButtonSelection>
                    </Grid>
                    <Grid item xs={12} lg={6} display={'flex'} alignItems={'center'}>
                        <Typography sx={titleStyle}>Style</Typography>
                        <TransparentButtonSelection options={styles.map(style => style.label)} onChange={handleStyleChange} value={selectedStyle.label}></TransparentButtonSelection>
                    </Grid>
                </Grid>
                {!socialPlatform && <DefaultSocialForm defaultFormState={socialFormState as DefaultSocialFormState} onFieldChange={onSocialFormStateChange}/>}
                {socialPlatform && socialPlatform.value === SocialPlatformNames.dailyMotion &&
                    <DailyMotionForm
                        dailyMotionFormState={socialFormState as DailyMotionFormState}
                        onFieldChange={onSocialFormStateChange}
                    />
                }
                {socialPlatform && socialPlatform.value === SocialPlatformNames.facebook &&
                    <FacebookForm
                        defaultFormState={socialFormState as FacebookFormState}
                        onFieldChange={onSocialFormStateChange}
                    />
                }
                {socialPlatform && socialPlatform.value === SocialPlatformNames.instagram &&
                    <InstagramForm
                        defaultFormState={socialFormState as InstagramFormState}
                        selectedPostType={socialPostType ? socialPostType.value : ''}
                        onFieldChange={onSocialFormStateChange}
                    />
                }
                {socialPlatform && socialPlatform.value === SocialPlatformNames.youtube &&
                    <YouTubeForm
                        youTubeFormState={socialFormState as YouTubeFormState}
                        onFieldChange={onSocialFormStateChange}
                    />
                }
                <Grid container width={'100%'} sx={{marginTop: theme.spacing.md}}>
                    <Grid item xs={6} display={'flex'} alignItems={'center'} >
                        <Box sx={{marginRight: theme.spacing.md}}>
                            <Typography sx={valueStyle}>Available Aspect Ratios</Typography>
                        </Box>
                        <Box display={'flex'} flexWrap={"wrap"} >
                            {aspectRatios.map((aspectRatio, index) => (
                                <Box display={'flex'} alignItems={'center'} sx={{marginRight: theme.spacing.sm}} key={'loading-AspectRation-' + index}>
                                    <TransparentButton
                                        text={aspectRatio.label} onClick={() => handleAspectRatioChange(aspectRatio.label)}
                                        selected={selectedAspectRatio.label === aspectRatio.label}
                                        disabled={!socialClips[aspectRatio.label]}
                                    />
                                    {socialClips[aspectRatio.label] ?
                                        <CheckCircleOutlineIcon sx={{color: theme.palette.white, marginLeft: theme.spacing.sm}}/> :
                                        <LoopIcon
                                            style={{animation: !socialClips[aspectRatio.label] ? "spin 1s linear infinite" : "none",}}
                                            sx={{color: theme.palette.white, marginLeft: theme.spacing.sm}}
                                        />
                                    }
                                </Box>
                            ))}
                        </Box>
                    </Grid>
                    <Grid item xs={6} display={'flex'} alignItems={'center'} gap={theme.spacing.md}>
                        <TransparentButton
                            text={"Generate Description"}
                            startIcon={descriptionsLoading ? <SpinningIcon/> : <CachedIcon/>}
                            onClick={handleRegenerateDescription}
                            disabled={descriptionsLoading}
                        />

                        <TransparentButton
                            text={"Save Description"}
                            startIcon={descriptionsSaveLoading ? <SpinningIcon/> : <SaveIcon/>}
                            onClick={handleDescriptionsSave}
                            disabled={descriptionsSaveLoading}
                        />

                        <TransparentButton
                            onClick={onPostPublication}
                            text={'Create Post'}
                            disabled={disablePostButton()}
                            startIcon={!publicationLoad ? <UploadIcon/> : <LoopIcon
                                style={{animation: "spin 1s linear infinite"}}
                                sx={{color: theme.palette.white, marginLeft: theme.spacing.sm}}
                            />}
                        />
                    </Grid>
                </Grid>
                <Grid container sx={{paddingTop: theme.spacing.md}} spacing={theme.spacing.md}>
                    {socialClips[defaultAspectRatioLabel] && <Grid item xs={12} lg={6} display={'flex'} direction={'column'} >
                        <Typography sx={valueStyle}>Video Preview (Masked)</Typography>
                        <Box ref={maskedPlayerContainerRef} sx={{position: 'relative'}}>
                            {maskedPlayerMuted ?
                                <VolumeOffIcon onClick={() => setMaskedPlayerMuted(prevState => !prevState)} sx={{position: 'absolute', zIndex: 99, top: 0, left:0, color: theme.palette.white}} /> :
                                <VolumeUpIcon onClick={() => setMaskedPlayerMuted(prevState => !prevState)} sx={{position: 'absolute', zIndex: 99, top: 0, left:0, color: theme.palette.white}} />
                            }
                            <WindowPlayer
                                url={socialClips[defaultAspectRatioLabel].preview_url}
                                frameRate={clip.frame_rate}
                                isPlaying={isMaskedPlaying}
                                setIsPlaying={setIsMaskedPlaying}
                                muted={!maskedPlayerMuted}
                                aspectRatio={selectedAspectRatio}
                                windowCoords={coordinates}
                                isActiveTime={true}
                                draggableWindow={false}
                                onCanPlay={onMaskedPlayerCanPlay}
                                changeVideoTime={changeMaskedPlayerTime}
                                loop={true}
                                shortcuts={false}
                                onEnded={onMaskedVideoEnd}
                            />
                        </Box>
                    </Grid>}
                    <Grid item xs={12} lg={6}>
                        <Typography sx={valueStyle}>Video Preview (Final)</Typography>
                        <Typography sx={valueStyle}></Typography>
                        <Box display={'flex'} justifyContent={'center'}>
                            <video
                                style={{width: 'auto', height: getSocialPlayerHeight()}}
                                ref={previewVideoPlayerRef}
                                onCanPlay={onPreviewPlayerCanPlay}
                                muted={true}
                                hidden={!(Boolean(socialClips[selectedAspectRatio.label]) && selectedAspectRatio.label !== defaultAspectRatioLabel)}
                            >
                                <source src={(socialClips[selectedAspectRatio.label] as any).social_clip_url ? (socialClips[selectedAspectRatio.label] as any).social_clip_url : socialClips[selectedAspectRatio.label].preview_url} />
                            </video>
                        </Box>
                    </Grid>
                </Grid>
                {publications.length > 0 && <Box paddingTop={theme.spacing.md}>
                    <PublicationHistory
                        rows={enrichedSocialPublications}
                        onPublicationDelete={handlePublicationDelete}
                    />
                </Box>}
            </Box>}
            {!socialClips[defaultAspectRatio.label] && <Box>
                <SpinnerBox height={'80vh'} text={'Computing Your Clip'}></SpinnerBox>
            </Box>}
        </Box>
    )
}

export default SocialPublish